/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { checkPostalCode } from "@eisinc/App/Utilities/helpers";
import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    postalCode: state.components.contactUsForm.fieldValues["postalCode"],
    country: state.components.contactUsForm.fieldValues["country"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PostalCodeStyles {
    postalCodeTextField?: TextFieldPresentationProps;
}

export const postalCodeStyles: PostalCodeStyles = {
    postalCodeTextField: {
        cssOverrides: {
            formField: css`
                margin-bottom: 15px;
                width: 45%;
                @media (max-width: 768px) {
                    width: 100%;
                }
            `,
        },
    },
};

const styles = postalCodeStyles;

const PostalCode: React.FC<Props> = ({ fields, postalCode, setFieldValue, country }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [postalCodeErrorMessage, setPostalCodeErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.postalCode = validatePostalCode;
        if (typeof postalCode !== "undefined") {
            validatePostalCode();
        }        
    }, [postalCode]);

    React.useEffect(() => {
        contactFormContext.validators.postalCode = validatePostalCode;
    }, [isRequired]);

    const validatePostalCode = () => {
        let errorMessage = isRequired && !postalCode ? (siteMessage("Field_Required", label) as string) : "";

        if (!errorMessage && country && postalCode) {
            if (checkPostalCode(country, postalCode)) {
                errorMessage = translate("Invalid Postal Code.");
            }
        }

        setPostalCodeErrorMessage(errorMessage);

        return !errorMessage;
    };

    const postalCodeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "postalCode", value: event.target.value });
    };


    return (
        <TextField
            {...styles.postalCodeTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={postalCode || ""}
            error={postalCodeErrorMessage}
            minLength={1}
            maxLength={50}
            onChange={postalCodeChangeHandler}
            disabled={!country}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PostalCode),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Postal Code",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "#####",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
